import { useQuery, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import * as apis from '@src/api/dimensions';
import { type DimensionMeta, type DimensionPayload } from './types';
import { message } from 'antd'; // Assuming you're using Ant Design for messages

export const useGetDimensionMeta = () => {
  return useQuery(
    ['get-dimension-meta'],
    () => {
      return apis.getDimensionMeta<DimensionMeta>();
    },
    {
      retry: 3,
      refetchOnWindowFocus: false,
    },
  );
};

export const useGetDimension = () => {
  return useQuery(
    ['get-saved-dimension'],
    () => {
      return apis.getDimensionData<DimensionPayload>();
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
    },
  );
};

export const useEditDimensions = () => {
  return useMutation(
    ({ dimensions }: { dimensions: DimensionPayload }) => {
      return apis.updateDimension(dimensions);
    },
    {
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.error || 'An error occurred while updating the dimension.';
        message.error(errorMessage);
      },
      onSuccess: (_, { dimensions }) => {
        const operation = dimensions.operation?.dimensionOperation || 'updated';
        const product = dimensions.operation?.product || 'Video/Ads';
        message.success(`${product} dimension ${operation}`);
      },
    },
  );
};

export const useModal = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalParams, setModalParams] = useState<{
    title: string;
    content: string;
    buttonText: string;
    onConfirm?: () => void;
  }>({ title: '', content: '', buttonText: 'OK' });

  const showModal = (params: typeof modalParams) => {
    setModalParams(params);
    setModalVisible(true);
  };

  const closeModal = () => setModalVisible(false);

  return {
    modalVisible,
    modalLoading,
    modalParams,
    showModal,
    closeModal,
    setModalLoading,
  };
};
